// pages/Ec2HealthHistory.jsx — history & trend views for the EC2 Health page.
// Data: cron ec2_health_snapshot (every 15 min) → tables ec2_health_snapshot / _disk (30 days)
// and WIB-day rollups ec2_health_daily / _disk_daily (2 years). See backend/ec2Health.js.
//   InstanceHistory  — inside an instance card's Details: CPU / RAM / disk charts + disk forecast
//   AccountTrends    — "History & trends" view: usage per instance + disk capacity forecast
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { api } from '../context/AuthContext';

const PALETTE = ['#0ea5e9', '#8b5cf6', '#f97316', '#10b981', '#ec4899', '#eab308', '#64748b', '#ef4444'];

// 'YYYY-MM-DD HH:MM:SS' is UTC (detail tables); 'YYYY-MM-DD' is a WIB day (rollups).
export function parseTs(s) {
  if (!s) return NaN;
  const str = String(s);
  if (str.length <= 10) return new Date(str + 'T00:00:00+07:00').getTime();
  return new Date(str.replace(' ', 'T') + 'Z').getTime();
}
function fmtTime(ms, days) {
  const d = new Date(ms);
  if (days > 30) return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
  if (days > 2) return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

const niceCeil = (v) => {
  if (!(v > 0)) return 1;
  const p = 10 ** Math.floor(Math.log10(v));
  const m = v / p;
  return (m <= 1 ? 1 : m <= 2 ? 2 : m <= 5 ? 5 : 10) * p;
};
const shortNum = (v) => (v >= 100 ? Math.round(v) : v >= 10 ? +v.toFixed(0) : v >= 1 ? +v.toFixed(1) : +v.toPrecision(2));
// Mbps → readable rate
export function fmtMbps(v) {
  if (v == null || !Number.isFinite(v)) return '—';
  if (v >= 1000) return `${(v / 1000).toFixed(2)} Gbps`;
  if (v >= 1) return `${v >= 100 ? v.toFixed(0) : v.toFixed(1)} Mbps`;
  if (v * 1000 >= 1) return `${(v * 1000).toFixed(0)} kbps`;
  return v > 0 ? '<1 kbps' : '0';
}

// Multi-series line chart, y = 0..100 (%), with warn/crit guide lines and a hover readout.
export function TrendChart({ series, days, warn, crit, height = 140, unit = '%', yMax }) {
  const ref = useRef(null);
  const [hover, setHover] = useState(null);
  const all = series.flatMap((s) => s.points);
  if (all.length < 2) {
    return <div className="flex h-24 items-center justify-center rounded-lg border border-dashed border-gray-200 text-xs text-gray-400">
      Not enough history yet — the snapshot runs every 15 minutes.</div>;
  }
  const W = 600, H = height, L = 30, R = 8, T = 8, B = 18;
  const t0 = Math.min(...all.map((p) => p[0])), t1 = Math.max(...all.map((p) => p[0]));
  const pct = unit === '%';
  const dataMax = Math.max(0, ...all.map((p) => p[1] || 0));
  const top = yMax || (pct ? Math.max(100, dataMax) : niceCeil(dataMax));
  const gridVals = pct ? [0, 25, 50, 75, 100].filter((v) => v <= top) : [0, 0.25, 0.5, 0.75, 1].map((f) => f * top);
  const x = (t) => L + ((t - t0) / Math.max(1, t1 - t0)) * (W - L - R);
  const y = (v) => T + (1 - v / top) * (H - T - B);
  // sampling interval = median spacing of the data (15 min, 1 h or 1 day depending on the source)
  const step = (() => {
    const ts = [...new Set(all.map((p) => p[0]))].sort((a, b) => a - b);
    const d = ts.slice(1).map((t, i) => t - ts[i]).sort((a, b) => a - b);
    return d.length ? d[Math.floor(d.length / 2)] : (days > 30 ? 86400e3 : days > 2 ? 3600e3 : 900e3);
  })();
  const path = (pts) => {
    let d = '';
    let prev = null;
    pts.forEach((p) => {
      if (p[1] == null) { prev = null; return; }
      // break the line on gaps longer than 3 sampling intervals
      const gap = prev && p[0] - prev[0] > 3 * step;
      d += `${!prev || gap ? 'M' : 'L'}${x(p[0]).toFixed(1)},${y(p[1]).toFixed(1)} `;
      prev = p;
    });
    return d;
  };
  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    const px = ((e.clientX - r.left) / r.width) * W;
    const t = t0 + ((px - L) / (W - L - R)) * (t1 - t0);
    setHover({ t, px });
  };
  const near = hover ? series.map((s) => {
    let best = null;
    s.points.forEach((p) => { if (p[1] != null && (!best || Math.abs(p[0] - hover.t) < Math.abs(best[0] - hover.t))) best = p; });
    return { name: s.name, color: s.color, p: best };
  }).filter((n) => n.p) : [];
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => t0 + f * (t1 - t0));
  return (
    <div className="relative">
      <svg ref={ref} viewBox={`0 0 ${W} ${H}`} className="h-auto w-full" onMouseMove={onMove} onMouseLeave={() => setHover(null)}>
        {gridVals.map((v) => (
          <g key={v}>
            <line x1={L} x2={W - R} y1={y(v)} y2={y(v)} stroke="#e5e7eb" strokeWidth="1" />
            <text x={L - 4} y={y(v) + 3} textAnchor="end" fontSize="9" fill="#9ca3af">{pct ? v : shortNum(v)}</text>
          </g>
        ))}
        {warn != null && <line x1={L} x2={W - R} y1={y(warn)} y2={y(warn)} stroke="#f59e0b" strokeDasharray="4 3" strokeWidth="1" opacity="0.7" />}
        {crit != null && <line x1={L} x2={W - R} y1={y(crit)} y2={y(crit)} stroke="#ef4444" strokeDasharray="4 3" strokeWidth="1" opacity="0.7" />}
        {ticks.map((t, i) => (
          <text key={i} x={x(t)} y={H - 4} textAnchor={i === 0 ? 'start' : i === 4 ? 'end' : 'middle'} fontSize="9" fill="#9ca3af">{fmtTime(t, days)}</text>
        ))}
        {series.map((s) => <path key={s.name} d={path(s.points)} fill="none" stroke={s.color} strokeWidth={s.dashed ? 1 : 1.6} strokeDasharray={s.dashed ? '3 2' : undefined} vectorEffect="non-scaling-stroke" />)}
        {hover && hover.px >= L && <line x1={hover.px} x2={hover.px} y1={T} y2={H - B} stroke="#94a3b8" strokeWidth="1" />}
      </svg>
      {hover && near.length > 0 && (
        <div className="pointer-events-none absolute right-1 top-1 rounded-md border border-gray-200 bg-white/95 px-2 py-1 text-[11px] shadow-sm">
          <div className="font-medium text-gray-600">{fmtTime(near[0].p[0], Math.min(days, 30))}</div>
          {near.map((n) => (
            <div key={n.name} className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full" style={{ background: n.color }} />
              <span className="text-gray-500">{n.name}</span>
              <span className="ml-auto pl-2 font-semibold tabular-nums text-gray-800">{pct ? `${n.p[1]}%` : fmtMbps(n.p[1])}</span>
            </div>
          ))}
        </div>
      )}
      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-gray-500">
        {series.map((s) => (
          <span key={s.name} className="inline-flex items-center gap-1"><span className="h-0.5 w-3" style={{ background: s.color }} />{s.name}</span>
        ))}
      </div>
    </div>
  );
}

const RANGES = [[1, '24h'], [7, '7d'], [30, '30d'], [90, '90d'], [365, '1y']];
export function RangePicker({ value, onChange, options = RANGES }) {
  return (
    <div className="inline-flex rounded-lg bg-slate-100/80 p-0.5 text-xs">
      {options.map(([d, l]) => (
        <button key={d} type="button" onClick={() => onChange(d)}
          className={`rounded-md px-2.5 py-1 font-medium ${value === d ? 'bg-white text-gray-900 shadow-sm ring-1 ring-gray-200' : 'text-gray-500 hover:text-gray-700'}`}>{l}</button>
      ))}
    </div>
  );
}

function fmtDays(n) {
  if (n == null) return '—';
  if (n <= 0) return 'now';
  if (n > 3650) return '> 10 y';
  if (n > 365) return `~${Math.round(n / 365)} y`;
  return `${n} d`;
}
function daysTone(n) {
  if (n == null) return 'text-gray-400';
  if (n <= 14) return 'font-semibold text-red-600';
  if (n <= 60) return 'font-semibold text-amber-600';
  return 'text-gray-700';
}

// ── Inside an instance's Details ─────────────────────────────────────────────
export function InstanceHistory({ account, instanceId, th }) {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [showAll, setShowAll] = useState(false);
  useEffect(() => {
    let off = false;
    setErr(null);
    api.get(`/api/ec2-health/history?account=${account}&instanceId=${encodeURIComponent(instanceId)}&days=${days}`)
      .then((r) => { if (!off) setData(r.data); })
      .catch((e) => { if (!off) setErr((e.response && e.response.data && e.response.data.error) || e.message); });
    return () => { off = true; };
  }, [account, instanceId, days]);

  const netSeries = useMemo(() => {
    if (!data) return [];
    const P = data.points.map((p) => Object.assign({ ms: parseTs(p.t) }, p));
    const s = [
      { name: 'In', color: '#10b981', points: P.map((p) => [p.ms, p.netIn]) },
      { name: 'Out', color: '#6366f1', points: P.map((p) => [p.ms, p.netOut]) },
    ];
    if (P.some((p) => p.netInMax != null)) {
      s.push({ name: 'In peak', color: '#10b981', dashed: true, points: P.map((p) => [p.ms, p.netInMax]) });
      s.push({ name: 'Out peak', color: '#6366f1', dashed: true, points: P.map((p) => [p.ms, p.netOutMax]) });
    }
    return s.filter((x) => x.points.some((p) => p[1] != null));
  }, [data]);
  const nicSeries = useMemo(() => {
    if (!data || !data.nics) return [];
    const nics = Object.entries(data.nics)
      .map(([name, rows]) => ({ name, rows, total: rows.reduce((a, r) => a + (r[1] || 0) + (r[2] || 0), 0) }))
      .filter((n) => n.total > 0).sort((a, b) => b.total - a.total).slice(0, 3);
    const out = [];
    nics.forEach((n, i) => {
      out.push({ name: `${n.name} rx`, color: PALETTE[(i * 2) % PALETTE.length], points: n.rows.map((r) => [parseTs(r[0]), r[1]]) });
      out.push({ name: `${n.name} tx`, color: PALETTE[(i * 2 + 1) % PALETTE.length], points: n.rows.map((r) => [parseTs(r[0]), r[2]]) });
    });
    return out.filter((x) => x.points.filter((p) => p[1] != null).length >= 2);
  }, [data]);
  const cpuMem = useMemo(() => {
    if (!data) return [];
    const P = data.points.map((p) => Object.assign({ ms: parseTs(p.t) }, p));
    const s = [
      { name: 'CPU avg', color: '#0ea5e9', points: P.map((p) => [p.ms, p.cpu]) },
      { name: 'CPU peak', color: '#0ea5e9', dashed: true, points: P.map((p) => [p.ms, p.cpuMax]) },
      { name: 'RAM', color: '#8b5cf6', points: P.map((p) => [p.ms, p.memMax ?? p.mem]) },
    ];
    if (P.some((p) => p.swap > 0)) s.push({ name: 'Swap', color: '#f97316', points: P.map((p) => [p.ms, p.swap]) });
    return s.filter((x) => x.points.some((p) => p[1] != null));
  }, [data]);

  const diskSeries = useMemo(() => {
    if (!data) return [];
    const ms = Object.entries(data.disks)
      .map(([mount, d]) => ({ mount, ignored: d.ignored, pts: d.points.map(([t, v]) => [parseTs(t), v]) }))
      .filter((d) => !d.ignored || showAll)
      .sort((a, b) => (b.pts.length ? b.pts[b.pts.length - 1][1] : 0) - (a.pts.length ? a.pts[a.pts.length - 1][1] : 0));
    return (showAll ? ms : ms.slice(0, 6)).map((d, i) => ({ name: d.mount, color: PALETTE[i % PALETTE.length], points: d.pts }));
  }, [data, showAll]);

  const trends = useMemo(() => (data ? data.trends.filter((t) => !t.ignored).sort((a, b) => (a.daysToFull ?? 1e9) - (b.daysToFull ?? 1e9) || (b.current ?? 0) - (a.current ?? 0)) : []), [data]);
  const totalMounts = data ? Object.values(data.disks).filter((d) => !d.ignored).length : 0;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">History</div>
        {data && data.historySince && <span className="text-[11px] text-gray-400">since {data.historySince} · {data.resolution} resolution</span>}
        <div className="ml-auto"><RangePicker value={days} onChange={setDays} /></div>
      </div>
      {err && <div className="text-xs text-red-600">{err}</div>}
      {!data && !err && <div className="text-xs text-gray-400">Loading history…</div>}
      {data && (
        <>
          <div>
            <div className="mb-1 text-xs font-medium text-gray-600">CPU & memory</div>
            <TrendChart series={cpuMem} days={days} warn={th.memWarn} crit={th.memCrit} />
          </div>
          {netSeries.length > 0 && (
            <div>
              <div className="mb-1 text-xs font-medium text-gray-600">Network throughput (CloudWatch, all interfaces)</div>
              <TrendChart series={netSeries} days={days} unit="Mbps" />
            </div>
          )}
          {nicSeries.length > 0 && (
            <div>
              <div className="mb-1 text-xs font-medium text-gray-600">Per interface (from the OS)</div>
              <TrendChart series={nicSeries} days={days} unit="Mbps" />
            </div>
          )}
          {diskSeries.length > 0 && (
            <div>
              <div className="mb-1 flex items-center text-xs font-medium text-gray-600">
                Disk used per mount {!showAll && totalMounts > 6 ? '(fullest 6)' : ''}
                <button type="button" onClick={() => setShowAll(!showAll)} className="ml-auto text-[11px] font-normal text-sky-600 hover:underline">
                  {showAll ? 'Show fewer' : 'Show all incl. DB devices'}
                </button>
              </div>
              <TrendChart series={diskSeries} days={days} warn={th.diskWarn} crit={th.diskCrit} />
            </div>
          )}
          {trends.length > 0 && (
            <div>
              <div className="mb-1 text-xs font-medium text-gray-600">Disk growth & forecast</div>
              <DiskTable rows={trends} showInstance={false} />
            </div>
          )}
        </>
      )}
    </div>
  );
}

function DiskTable({ rows, showInstance, onPick }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-gray-200 text-left text-[11px] uppercase tracking-wide text-gray-500">
            {showInstance && <th className="py-1 pr-2 font-medium">Instance</th>}
            <th className="py-1 pr-2 font-medium">Mount</th>
            <th className="py-1 pr-2 text-right font-medium">Size</th>
            <th className="py-1 pr-2 text-right font-medium">Used now</th>
            <th className="py-1 pr-2 text-right font-medium">Change</th>
            <th className="py-1 pr-2 text-right font-medium">Growth / day</th>
            <th className="py-1 pr-2 text-right font-medium">To critical</th>
            <th className="py-1 text-right font-medium">To full</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.instanceId + r.mount} className={`border-b border-gray-100 ${onPick ? 'cursor-pointer hover:bg-gray-50' : ''}`} onClick={onPick ? () => onPick(r) : undefined}>
              {showInstance && <td className="max-w-[180px] truncate py-1 pr-2 text-gray-800" title={r.name}>{r.name}</td>}
              <td className="max-w-[200px] truncate py-1 pr-2 text-gray-700" title={r.mount}>{r.mount}</td>
              <td className="py-1 pr-2 text-right tabular-nums text-gray-500">{r.sizeGiB != null ? `${r.sizeGiB} GiB` : '—'}</td>
              <td className="py-1 pr-2 text-right tabular-nums font-medium text-gray-800">{r.current != null ? `${r.current}%` : '—'}</td>
              <td className={`py-1 pr-2 text-right tabular-nums ${r.change > 0 ? 'text-amber-700' : r.change < 0 ? 'text-emerald-700' : 'text-gray-400'}`}>
                {r.change != null ? `${r.change > 0 ? '+' : ''}${r.change} pt` : '—'}</td>
              <td className="py-1 pr-2 text-right tabular-nums text-gray-600">
                {r.pctPerDay == null ? <span className="text-gray-400" title="Needs at least 24 h of history">collecting…</span>
                  : `${r.pctPerDay > 0 ? '+' : ''}${r.pctPerDay}%${r.gibPerDay != null ? ` · ${r.gibPerDay} GiB` : ''}`}</td>
              <td className={`py-1 pr-2 text-right tabular-nums ${daysTone(r.daysToCrit)}`}>{r.current >= 90 ? <span className="text-red-600">already</span> : fmtDays(r.daysToCrit)}</td>
              <td className={`py-1 text-right tabular-nums ${daysTone(r.daysToFull)}`}>{fmtDays(r.daysToFull)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function csvDownload(name, header, rows) {
  const esc = (v) => { const s = v == null ? '' : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  const blob = new Blob(['﻿' + [header, ...rows].map((r) => r.map(esc).join(',')).join('\n')], { type: 'text/csv;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob); a.download = name; a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 2000);
}

// ── Account-wide "History & trends" view ─────────────────────────────────────
export function AccountTrends({ account, scopeIds, onOpenInstance }) {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = useCallback(() => {
    setLoading(true); setErr(null);
    api.get(`/api/ec2-health/trends?account=${account}&days=${days}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr((e.response && e.response.data && e.response.data.error) || e.message))
      .finally(() => setLoading(false));
  }, [account, days]);
  useEffect(() => { load(); }, [load]);

  const inScope = useCallback((id) => !scopeIds || scopeIds.has(id), [scopeIds]);
  const inst = useMemo(() => (data ? data.instances.filter((i) => inScope(i.id))
    .sort((a, b) => (b.critSamples ?? b.critDays ?? 0) - (a.critSamples ?? a.critDays ?? 0) || (b.memMax ?? 0) - (a.memMax ?? 0)) : []), [data, inScope]);
  const disks = useMemo(() => (data ? data.disks.filter((d) => inScope(d.instanceId)) : []), [data, inScope]);
  const atRisk = disks.filter((d) => d.daysToFull != null && d.daysToFull <= 60);

  if (err) return <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{err}</div>;
  if (!data) return <div className="p-8 text-center text-sm text-gray-400">Loading history…</div>;
  const th = data.thresholds;
  const bySamples = data.unit === 'samples';
  const pctTime = (i) => {
    const c = bySamples ? i.critSamples : i.critDays;
    const w = bySamples ? i.warnSamples : i.warnDays;
    const n = bySamples ? i.samples : Math.max(1, Math.round(days));
    return { c: n ? Math.round((c / n) * 1000) / 10 : 0, w: n ? Math.round((w / n) * 1000) / 10 : 0 };
  };
  const cell = (v, warn, crit) => (v == null ? <span className="text-gray-300">—</span>
    : <span className={v >= crit ? 'font-semibold text-red-600' : v >= warn ? 'font-semibold text-amber-600' : 'text-gray-700'}>{v}%</span>);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="text-sm text-gray-500">
          History since <span className="font-medium text-gray-700">{data.historySince || 'today'}</span>
          {' · '}{bySamples ? '15-min samples' : 'daily rollups'}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <RangePicker value={days} onChange={setDays} options={[[7, '7d'], [30, '30d'], [90, '90d'], [365, '1y'], [730, '2y']]} />
          <button type="button" onClick={load} disabled={loading} className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
            {loading ? 'Loading…' : 'Reload'}</button>
        </div>
      </div>

      {atRisk.length > 0 && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          <span className="font-semibold">{atRisk.length} filesystem{atRisk.length > 1 ? 's' : ''} projected to fill within 60 days: </span>
          {atRisk.slice(0, 5).map((d) => `${d.name} ${d.mount} (${fmtDays(d.daysToFull)})`).join(' · ')}
        </div>
      )}

      <section className="rounded-xl border border-gray-200 bg-white p-4">
        <div className="mb-2 flex items-center">
          <h3 className="text-sm font-semibold text-gray-800">Disk capacity forecast</h3>
          <span className="ml-2 text-[11px] text-gray-400">linear trend over the selected window · Sybase DB devices excluded</span>
          <button type="button" className="ml-auto text-xs text-sky-600 hover:underline" onClick={() => csvDownload(`ec2-disk-forecast-${account}-${days}d.csv`,
            ['Instance', 'Mount', 'Size GiB', 'Used %', 'Change pt', 'Growth %/day', 'Growth GiB/day', 'Days to critical', 'Days to full'],
            disks.map((d) => [d.name, d.mount, d.sizeGiB, d.current, d.change, d.pctPerDay, d.gibPerDay, d.daysToCrit, d.daysToFull]))}>Export CSV</button>
        </div>
        {disks.length ? <DiskTable rows={disks} showInstance onPick={(r) => onOpenInstance && onOpenInstance(r.instanceId)} />
          : <div className="text-xs text-gray-400">No disk history yet.</div>}
      </section>

      <section className="rounded-xl border border-gray-200 bg-white p-4">
        <div className="mb-2 flex items-center">
          <h3 className="text-sm font-semibold text-gray-800">Usage per instance</h3>
          <span className="ml-2 text-[11px] text-gray-400">avg / peak over the selected window</span>
          <button type="button" className="ml-auto text-xs text-sky-600 hover:underline" onClick={() => csvDownload(`ec2-usage-${account}-${days}d.csv`,
            ['Instance', 'CPU avg %', 'CPU peak %', 'RAM avg %', 'RAM peak %', 'Swap peak %', 'Worst disk peak %', 'Net in avg Mbps', 'Net out avg Mbps', 'Net in peak Mbps', 'Net out peak Mbps', '% time critical', '% time warning'],
            inst.map((i) => { const p = pctTime(i); return [i.name, i.cpuAvg, i.cpuMax, i.memAvg, i.memMax, i.swapMax, i.diskMax, i.netInAvg, i.netOutAvg, i.netInMax, i.netOutMax, p.c, p.w]; }))}>Export CSV</button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-gray-200 text-left text-[11px] uppercase tracking-wide text-gray-500">
                <th className="py-1 pr-2 font-medium">Instance</th>
                <th className="py-1 pr-2 text-right font-medium">CPU avg</th>
                <th className="py-1 pr-2 text-right font-medium">CPU peak</th>
                <th className="py-1 pr-2 text-right font-medium">RAM avg</th>
                <th className="py-1 pr-2 text-right font-medium">RAM peak</th>
                <th className="py-1 pr-2 text-right font-medium">Swap peak</th>
                <th className="py-1 pr-2 text-right font-medium">Worst disk</th>
                <th className="py-1 pr-2 text-right font-medium">Net peak in / out</th>
                <th className="py-1 text-right font-medium">Time critical / warning</th>
              </tr>
            </thead>
            <tbody>
              {inst.map((i) => {
                const p = pctTime(i);
                return (
                  <tr key={i.id} className="cursor-pointer border-b border-gray-100 hover:bg-gray-50" onClick={() => onOpenInstance && onOpenInstance(i.id)}>
                    <td className="max-w-[220px] truncate py-1 pr-2 text-gray-800" title={i.name}>{i.name}</td>
                    <td className="py-1 pr-2 text-right tabular-nums">{cell(i.cpuAvg, th.cpuWarn, th.cpuCrit)}</td>
                    <td className="py-1 pr-2 text-right tabular-nums">{cell(i.cpuMax, th.cpuWarn, th.cpuCrit)}</td>
                    <td className="py-1 pr-2 text-right tabular-nums">{cell(i.memAvg, th.memWarn, th.memCrit)}</td>
                    <td className="py-1 pr-2 text-right tabular-nums">{cell(i.memMax, th.memWarn, th.memCrit)}</td>
                    <td className="py-1 pr-2 text-right tabular-nums">{cell(i.swapMax, th.swapWarn, 101)}</td>
                    <td className="py-1 pr-2 text-right tabular-nums">{cell(i.diskMax, th.diskWarn, th.diskCrit)}</td>
                    <td className="whitespace-nowrap py-1 pr-2 text-right tabular-nums text-gray-600">{i.netInMax == null ? <span className="text-gray-300">—</span> : `${fmtMbps(i.netInMax)} / ${fmtMbps(i.netOutMax)}`}</td>
                    <td className="py-1 text-right tabular-nums">
                      <span className={p.c ? 'font-semibold text-red-600' : 'text-gray-400'}>{p.c}%</span>
                      <span className="text-gray-300"> / </span>
                      <span className={p.w ? 'text-amber-600' : 'text-gray-400'}>{p.w}%</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {!inst.length && <div className="text-xs text-gray-400">No history yet.</div>}
      </section>
      <div className="text-[11px] text-gray-400">
        Snapshot every 15 minutes (cron <code>ec2_health_snapshot</code>). Detail kept {30} days; daily rollups (WIB) kept 2 years.
        Forecast needs at least 24 hours of history and is a straight-line projection — treat it as an early warning, not a precise date.
        Click a row to open that instance.
      </div>
    </div>
  );
}
